import React, { useState, useEffect} from 'react';
import { RefreshCw } from 'lucide-react';
import { 
  SharePointData, 
  ExcludedItems, 
  SharePointPreviewTabProps,
  SharePointItem
} from '../../types/sharepoint.types';
import SharePointTab from "./SharePointTab";
import SharePointToggleSwitch from "./SharePointToggleSwitch";
import "../../styles/loader.css";
import { sharepointTabConfig } from "../../constants/sharepointTabConfig";

type VisibilityKey = 'lists' | 'document_libraries';

const SharePointPreviewTab: React.FC<SharePointPreviewTabProps> = ({
  selectedSharepointData,
  activeSubTabs,
  setActiveSubTabs,
  checkedItems,
  isLoadingTabs,
  showDocumentCache,
  isRefreshing,
  onRefresh,
  onCheckboxChange,
  onToggleChange,
  updateExcludedItems,
  onItemClick,
  onBackToLists,
  onNavigateNext,
  onNavigatePrevious,
  onPageSizeChange,
  onDocumentExclusion,
  onLibraryExclusion,
  isDocumentExcluded,
  getLibrarySelectionState
}) => {
  const [visibility] = useState({
    lists: true,
    document_libraries: true,
  });

    // Add this useEffect after the visibility useState
useEffect(() => {
  if (selectedSharepointData?.sharepointItems) {
    // Find first tab with data
    const availableTabs = ['lists', 'document_libraries'].filter((tab) => {
      const items = selectedSharepointData.sharepointItems[tab] || [];
      return items.length > 0;
    });
    
    // Set to first available tab if current tab has no data or is not available
    if (availableTabs.length > 0) {
      const currentTabHasData = selectedSharepointData.sharepointItems[activeSubTabs]?.length > 0;
      if (!currentTabHasData) {
        setActiveSubTabs(availableTabs[0] as keyof typeof sharepointTabConfig);
      }
    }
  }
}, [selectedSharepointData?.id, selectedSharepointData?.sharepointItems]);

  // NEW: Add search state management
  const [searchTerm, setSearchTerm] = useState("");


  console.log('📱 SharePointPreviewTab rendered with:', {
    selectedSharepointId: selectedSharepointData?.id,
    excludedLibraries: selectedSharepointData?.excludedItems?.excluded_document_libraries || []
  });

  const handleTabChange = (key: string) => {
    if (key === 'lists' || key === 'document_libraries') {
      if (onBackToLists && selectedSharepointData?.id) {
        onBackToLists(selectedSharepointData.id);
      }
      
      setActiveSubTabs(key as keyof typeof sharepointTabConfig);
      // Reset search when changing tabs
      setSearchTerm("");
    }
  };

  const handleInsideToggleChange = (key: VisibilityKey, sharepointId: number) => {
    console.log('🔴 TOGGLE CLICKED:', { key, sharepointId });
    console.log('🔴 BEFORE - selectedSharepointData.visibility:', selectedSharepointData.visibility);
    
    onToggleChange(key, sharepointId);
    
    // Add a timeout to check if state updated
    setTimeout(() => {
      console.log('🔴 AFTER - selectedSharepointData.visibility:', selectedSharepointData.visibility);
    }, 100);
  };

  // NEW: Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  
  const handleItemClickInternal = (item: SharePointItem, itemType: 'lists' | 'document_libraries') => {
    if (onItemClick && selectedSharepointData?.id) {
      onItemClick(item, itemType, selectedSharepointData.id);
    }
  };

  const handleBackToListsInternal = () => {
    if (onBackToLists && selectedSharepointData?.id) {
      onBackToLists(selectedSharepointData.id);
      // Reset search when going back
      setSearchTerm("");
    }
  };

  const handleNavigateNextInternal = async (): Promise<void> => {
    if (onNavigateNext && selectedSharepointData?.id) {
      try {
        await onNavigateNext(selectedSharepointData.id);
      } catch (error) {
        console.error('Failed to navigate next in preview tab:', error);
      }
    }
  };

  const handleNavigatePreviousInternal = async (): Promise<void> => {
    if (onNavigatePrevious && selectedSharepointData?.id) {
      try {
        await onNavigatePrevious(selectedSharepointData.id);
      } catch (error) {
        console.error('Failed to navigate previous in preview tab:', error);
      }
    }
  };

  const handlePageSizeChangeInternal = async (pageSize: number): Promise<void> => {
    if (onPageSizeChange && selectedSharepointData?.id) {
      try {
        await onPageSizeChange(selectedSharepointData.id, pageSize);
      } catch (error) {
        console.error('Failed to change page size in preview tab:', error);
      }
    }
  };

  // Add this helper function at the top of SharePointPreviewTab.tsx (after imports, before the component)

const getContextualHeader = (selectedSharepointData: SharePointData): string => {
  const siteName = selectedSharepointData.sharepoint_name || 'SharePoint Preview';
  const urlAnalysis = selectedSharepointData.url_analysis;
  
  // If no URL analysis or site-level connection, just return site name
  if (!urlAnalysis || urlAnalysis.url_type === 'site') {
    return siteName;
  }

  try {
    switch (urlAnalysis.url_type) {
      case 'list': {
        // Find the specific list by target_list_id
        const targetListId = urlAnalysis.target_list_id;
        if (targetListId && selectedSharepointData.sharepointItems?.lists) {
          const targetList = selectedSharepointData.sharepointItems.lists.find(
            list => list.id === targetListId
          );
          if (targetList) {
            const listName = targetList.title || targetList.display_name || targetList.name || 'Unknown List';
            return `${listName} (List)`;
          }
        }
        return `${siteName} → Specific List`;
      }

      case 'library': {
        // Find the specific library by target_library_id
        const targetLibraryId = urlAnalysis.target_library_id;
        if (targetLibraryId && selectedSharepointData.sharepointItems?.document_libraries) {
          const targetLibrary = selectedSharepointData.sharepointItems.document_libraries.find(
            library => library.id === targetLibraryId || library.drive_id === targetLibraryId
          );
          if (targetLibrary) {
            const libraryName = targetLibrary.title || targetLibrary.display_name || targetLibrary.name || 'Unknown Library';
            return `${libraryName} (Document Library)`;
          }
        }
        return `${siteName} → Document Library`;
      }

      case 'folder': {
        // Find the library and add folder path
        const targetLibraryId = urlAnalysis.target_library_id;
        const folderPath = urlAnalysis.target_folder_path;
        
        if (targetLibraryId && selectedSharepointData.sharepointItems?.document_libraries) {
          const targetLibrary = selectedSharepointData.sharepointItems.document_libraries.find(
            library => library.id === targetLibraryId || library.drive_id === targetLibraryId
          );
          
          if (targetLibrary) {
            const libraryName = targetLibrary.title || targetLibrary.display_name || targetLibrary.name || 'Documents';
            
            if (folderPath) {
              // Truncate very long folder paths for display
              const displayPath = folderPath.length > 30 
                ? `.../${folderPath.split('/').pop()}` 
                : folderPath;
              return `${siteName} → ${libraryName}/${displayPath}`;
            } else {
              return `${siteName} → ${libraryName}`;
            }
          }
        }
        
        // Fallback if library not found but we have folder path
        if (folderPath) {
          const displayPath = folderPath.length > 30 
            ? `.../${folderPath.split('/').pop()}` 
            : folderPath;
          return `${siteName} → ${displayPath}`;
        }
        
        return `${siteName} → Folder`;
      }

      default:
        return siteName;
    }
  } catch (error) {
    console.error('Error building contextual header:', error);
    return siteName; // Fallback to just site name on error
  }
};

// Then in your JSX, replace this line:
// {selectedSharepointData.sharepoint_name || 'SharePoint Preview'}

// With this:
// {getContextualHeader(selectedSharepointData)}

  const handleDocumentExclusionInternal = (
    sharepointId: number,
    documentId: string,
    libraryId: string,
    exclude: boolean
  ) => {
    if (onDocumentExclusion) {
      onDocumentExclusion(sharepointId, documentId, libraryId, exclude);
    }
  };

  const handleLibraryExclusionInternal = (
    sharepointId: number,
    libraryId: string,
    exclude: boolean
  ) => {
    if (onLibraryExclusion) {
      onLibraryExclusion(sharepointId, libraryId, exclude);
    }
  };

  const handleRefreshClick = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  if (!selectedSharepointData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl font-semibold mb-2 text-gray-500">No SharePoint Selected</h3>
          <p className="text-gray-400">Please select a SharePoint source to preview its contents.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex justify-between items-center p-4 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-800">
            {getContextualHeader(selectedSharepointData)}
          </h3>
          {selectedSharepointData.drillDownState?.isViewingItems && (
            <span className="text-sm text-gray-500">
              → {selectedSharepointData.drillDownState.selectedItem?.title || 'Items'}
            </span>
          )}
        </div>
        
        {/* Refresh Button - Similar to Canvas */}
        {showDocumentCache && selectedSharepointData.connectionState === 'connected' && (
          <button
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-black border border-gray-500 rounded-lg text-white font-semibold hover:bg-gray-800 hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh SharePoint data"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Preview'}
            <RefreshCw 
              size={18} 
              className={isRefreshing ? 'animate-spin' : ''} 
            />
          </button>
        )}
      </div>

      <div className="flex border rounded-lg p-4">
    

        <div className={`${
          selectedSharepointData.drillDownState?.isViewingItems ? 'w-full' : 'w-[100%] pl-4'
        } overflow-y-auto`}>
          {selectedSharepointData.error ? (
            <div className="text-red-600 text-center py-4">
              <div className="text-xl font-semibold mb-2">Error Loading SharePoint Data</div>
              <p className="text-gray-600">{selectedSharepointData.error}</p>
            </div>
          ) : (
            <>
              {['lists', 'document_libraries'].map((tab) => {
                const items = (selectedSharepointData?.sharepointItems as { [key: string]: any[] })?.[tab] ?? [];
                const hasData = items.length > 0;
                const displayName = tab === 'lists' ? 'Lists' : 'Document Libraries';
                const ismatchSubTab = activeSubTabs === tab;
                const matchSubTab = activeSubTabs === 'lists' ? 'Lists' : 'Document Libraries';
                const sharepointId = selectedSharepointData.id;
                
                const isVisible = selectedSharepointData.visibility?.[tab as keyof typeof selectedSharepointData.visibility] ?? true;
                const isTabLoading = isLoadingTabs[sharepointId]?.[tab];
                const drillDownState = selectedSharepointData.drillDownState;

                return (
                  <div className="w-full max-h-80 overflow-y-auto" key={tab}>
                    {ismatchSubTab ? (
                      <>
                        {isTabLoading ? (
                          <div className="flex justify-center items-center h-20">
                            <div className="dotted-circle">
                              {[...Array(10)].map((_, i) => (
                                <span key={i} className="dot" style={{ "--i": i } as React.CSSProperties}></span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            {hasData ? (
                              <>
                         
                                
                                <div className={`transition-opacity duration-300 ${
                                  !drillDownState?.isViewingItems && !isVisible ? 'opacity-50 pointer-events-none' : ''
                                }`}>
                                  <SharePointTab
                                    activeSubTab={activeSubTabs as "lists" | "document_libraries"}
                                    displayName={displayName}
                                    matchSubTab={matchSubTab}
                                    visibility={visibility}
                                    items={items}
                                    itemCount={items.length}
                                    handleTabChange={handleTabChange}
                                    handleCheckboxChange={onCheckboxChange}
                                    checkedItems={checkedItems}
                                    sharepointId={Array.isArray(sharepointId) ? 0 : sharepointId ?? 0}
                                    excludedItems={selectedSharepointData.excludedItems}
                                    updateExcludedItems={updateExcludedItems}
                                    selectedItems={selectedSharepointData.selectedItems}
                                    drillDownState={drillDownState}
                                    onItemClick={handleItemClickInternal}
                                    onBackToLists={handleBackToListsInternal}
                                    onNavigateNext={handleNavigateNextInternal}
                                    onNavigatePrevious={handleNavigatePreviousInternal}
                                    onPageSizeChange={handlePageSizeChangeInternal}
                                    onDocumentExclusion={onDocumentExclusion}
                                    onLibraryExclusion={onLibraryExclusion}
                                    isDocumentExcluded={isDocumentExcluded}
                                    getLibrarySelectionState={getLibrarySelectionState}
                                    sharepoint={selectedSharepointData}
                                    searchTerm={searchTerm}
                                  />
                                </div>
                              </>
                            ) : (
                              <div className="flex items-center justify-center h-full w-full text-gray-500">
                                <div className="text-center">
                                  <h3 className="text-xl font-semibold mb-4">No data found</h3>
                                  <p className="text-gray-400">
                                    {selectedSharepointData.connectionState === 'connected' 
                                      ? `No ${displayName.toLowerCase()} are available in this SharePoint source.`
                                      : 'Please connect to SharePoint to view data.'
                                    }
                                  </p>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharePointPreviewTab;